import { formatEpisodeDetails, formatTreatmentDetails, hasInErrorReason, } from "./helpers.js";
class LeaveIntermittentSearch {
    constructor() {
        this.format = (response) => {
            const searchResultMetadata = this.extractSearchMetadata(response);
            const LeaveSearch = this.processLeaves(response.leaves);
            // Apply UI-level concatenation based on intermittentDt + protectionStatusDesc
            const finalLeaveSearch = this.groupProtections(LeaveSearch);
            return { searchResultMetadata, LeaveSearch: finalLeaveSearch };
        };
        this.extractSearchMetadata = (response) => ({
            startIndex: response.searchResultMetaData?.startIndex,
            totalResults: response.searchResultMetaData?.totalResults,
            itemsPerPage: response.searchResultMetaData?.itemsPerPage,
            timeTaken: response.searchResultMetaData?.resultMetaData?.timeTaken,
            links: response.searchResultMetaData?.resultMetaData?._links
        });
        this.processLeaves = (leaves = []) => {
            const LeaveSearch = [];
            leaves.forEach((leave) => {
                if (!leave.employee ||
                    !leave.leavePeriod ||
                    hasInErrorReason(leave.leaveCloseReason, leave.leaveCloseReasonDesc)) {
                    return;
                }
                leave.leavePeriod.forEach((period) => {
                    const periodEntries = this.processPeriod(leave, period);
                    LeaveSearch.push(...periodEntries);
                });
            });
            return LeaveSearch;
        };
        this.processPeriod = (leave, period) => {
            const isNavilink = leave.leaveSourceSystem === "NVLNK";
            if (hasInErrorReason(period?.periodStatusReason, period?.leavePeriodStatusReasonDesc)) {
                return [];
            }
            const protections = this.getProtections(period);
            const absences = this.getAbsences(period);
            const certificationDetail = this.getPrimaryCertificationDetail(period);
            const entries = [];
            // Treat all systems the same: create one entry per protection per absence
            if (isNavilink) {
                protections.forEach((protection) => {
                    absences.forEach((absence) => {
                        const entry = this.createEntry(leave, period, protection, absence, certificationDetail);
                        entries.push(entry);
                    });
                });
            }
            else {
                absences.forEach((absence) => {
                    const matchedProtection = protections.find((p) => p.protectionId === absence.protectionId) ||
                        this.getDefaultProtection();
                    const entry = this.createEntry(leave, period, matchedProtection, absence, certificationDetail);
                    entries.push(entry);
                });
            }
            return entries;
        };
        this.getProtections = (period) => period.leavePeriodProtection?.length
            ? period.leavePeriodProtection
            : [this.getDefaultProtection()];
        this.getAbsences = (period) => {
            const absences = period.intermittentAbsenceDetails?.length
                ? period.intermittentAbsenceDetails
                : [this.getDefaultAbsence()];
            return absences.filter((absence) => !hasInErrorReason(absence?.decisionReasonDesc));
        };
        this.getDefaultProtection = () => ({
            protectionId: "",
            protectionName: "",
            protectionDisplayName: "",
            protectionType: "",
            srcProtectionType: "",
            protectionStatusReasonDesc: "",
            protectionStatusDesc: ""
        });
        this.getDefaultAbsence = () => ({
            reportedDt: "",
            intermittentType: "",
            intermittentDt: "",
            startTime: "",
            endTime: "",
            intermittentAbsenceDurationHoursQty: "",
            intermittentAbsenceDurationMinutesQty: "",
            intermittentAbsenceDisplayName: "",
            deductionOvertimeInd: "",
            requestedHours: ""
        });
        this.lookupProtectionName = (period, protectionId) => {
            if (!period?.leavePeriodProtection)
                return null;
            const protection = period.leavePeriodProtection.find((p) => p.protectionId === protectionId);
            return protection?.protectionName || null;
        };
        this.getPrimaryCertificationDetail = (period) => {
            return (period.leavePeriodDocuments || [])
                .flatMap((doc) => doc.documentLatestRevision?.certificationDetails || [])
                .find((certificationDetail) => certificationDetail?.treatmentDetails ||
                certificationDetail?.episodeDetails);
        };
        this.createEntry = (leave, period, protection, absence, certificationDetail) => {
            const isNavilink = leave.leaveSourceSystem === "NVLNK";
            const statusValue = isNavilink
                ? protection?.protectionStatusDesc
                : absence?.intermittentStatus;
            const isPending = [
                "Pending",
                "Pending Certification",
                "Time Available",
                "Waiting Period",
            ].includes(statusValue ?? "");
            const isApproved = statusValue === "Approved" ||
                statusValue === "Approved - Non-count";
            const isDenied = statusValue === "Denied" || statusValue === "Denied - ADA";
            // Always treat protection as a single object
            const protectionNameValue = this.lookupProtectionName(period, protection?.protectionId || "");
            const protectionStatusDescValue = protection?.protectionStatusDesc || null;
            const planDurationDesc = protection?.protectionHours?.planDurationDesc || null;
            const intermittentStatusValue = this.getIntermittentStatus(leave, protection, absence);
            const entry = {
                coverageType: leave.coverageType,
                age: leave.ageAtLeaveStart,
                employeeId: leave.employee?.employeeId,
                firstName: leave.employee?.firstName,
                lastName: leave.employee?.lastName,
                fullName: leave.employee?.name,
                taxIdentificationNumber: leave.employee?.taxIdentificationNumber,
                intermittentAbsenceDurationHoursQty: absence?.intermittentAbsenceDurationHoursQty || null,
                reportedDt: absence?.reportedDt || null,
                intermittentType: absence?.intermittentType || null,
                intermittentDt: absence?.intermittentDt || null,
                startTime: absence?.startTime || null,
                endTime: absence?.endTime || null,
                intermittentAbsenceDurationMinutesQty: absence?.intermittentAbsenceDurationMinutesQty || null,
                intermittentAbsenceDisplayName: this.formatAbsenceDisplayName(absence),
                deductionOvertimeInd: absence?.deductionOvertimeInd || null,
                intermittentStatus: isPending
                    ? "Pending"
                    : statusValue,
                approvedHours: isApproved
                    ? Number(absence.requestedHours).toFixed(2)
                    : null,
                deniedHours: isDenied
                    ? Number(absence.requestedHours).toFixed(2)
                    : null,
                pendingHours: isPending
                    ? Number(absence.requestedHours).toFixed(2)
                    : null,
                historicalLeaveInd: leave.historicalLeaveInd,
                leaveId: leave.leaveId,
                leavePeriodId: period.leavePeriodId,
                periodEndDt: period.periodEndDt,
                leavePeriodNbr: period.leavePeriodNbr,
                leaveScheduleType: period.leaveScheduleType,
                periodStartDt: period.periodStartDt,
                reportGroupName: period.reportGroupName,
                reportGroupNumber: period.reportGroupNumber,
                employerReportGroupNumber: period.employerReportGroupNumber,
                leaveReason: leave.leaveReason,
                leaveStatus: leave.leaveStatus,
                protectionId: protection?.protectionId,
                protectionName: protectionNameValue,
                protectionStatusDesc: protectionStatusDescValue,
                requestedHours: absence?.requestedHours !== null && absence?.requestedHours !== undefined
                    ? Number(absence.requestedHours).toFixed(2)
                    : null,
                absenceDecisionReason: absence?.decisionReasonDesc,
                normalWorkingHours: absence?.normalWorkingHours || null,
                planDurationDesc: planDurationDesc,
                intermittentStatusValue: intermittentStatusValue,
                treatmentDetail: formatTreatmentDetails(certificationDetail?.treatmentDetails, leave.leaveSourceSystem || ""),
                episodeDetail: formatEpisodeDetails(certificationDetail?.episodeDetails, leave.leaveSourceSystem || ""),
                certBeginDate: certificationDetail?.beginDt,
                certEndDate: certificationDetail?.endDt,
            };
            return entry;
        };
        this.formatAbsenceDisplayName = (absence) => {
            if (!absence ||
                absence.durationHours === undefined ||
                absence.durationMinutes === undefined) {
                return null;
            }
            const minutes = String(absence.durationMinutes).padStart(2, "0");
            const hours = String(absence.durationHours).padStart(2, "0");
            return `${hours}:${minutes}`;
        };
        this.getIntermittentStatus = (leave, protection, absence) => {
            return leave.leaveSourceSystem === "NVLNK"
                ? protection?.protectionStatusDesc ?? null
                : absence?.intermittentStatus ?? null;
        };
        this.groupProtections = (entries) => {
            const grouped = [];
            const normalize = (v) => (v === null || v === undefined ? "" : String(v).trim());
            const normalizeTime = (v) => {
                if (!v)
                    return "";
                const t = new Date(v).getTime();
                return isNaN(t) ? normalize(v) : t;
            };
            entries.forEach((entry) => {
                const existing = grouped.find((g) => normalize(g.employeeId) === normalize(entry.employeeId) &&
                    normalize(g.leaveId) === normalize(entry.leaveId) &&
                    normalize(g.leavePeriodId) === normalize(entry.leavePeriodId) &&
                    normalizeTime(g.intermittentDt) === normalizeTime(entry.intermittentDt) &&
                    normalize(g.intermittentStatusValue) === normalize(entry.intermittentStatusValue) &&
                    normalizeTime(g.startTime) === normalizeTime(entry.startTime) &&
                    normalizeTime(g.endTime) === normalizeTime(entry.endTime));
                if (!existing) {
                    grouped.push({
                        ...entry,
                        protectionNames: entry.protectionName
                            ? [entry.protectionName]
                            : [],
                    });
                }
                else {
                    if (entry.protectionName &&
                        !existing.protectionNames.includes(entry.protectionName)) {
                        existing.protectionNames.push(entry.protectionName);
                    }
                }
            });
            return grouped.map((group) => ({
                ...group,
                protectionName: group.protectionNames.join(", "),
            }));
        };
    }
}
export default new LeaveIntermittentSearch();
